import { useQuery, useMutation, useInfiniteQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { getProducts, getProduct, getProductBySlug, checkAvailability, getAvailableLocations, getRelatedProducts } from '@/api/catalog';
import { CustomerEquipmentTypeSearchRequest, CustomerEquipmentTypeAvailabilityRequest } from '@/types';
import { useLocationStore } from '@/stores/useLocationStore';

// Product/pricing freshness policy: until a webhook-driven rebuild lands
// (follow-up issue #383), live admin price edits should propagate to the
// customer site on every page mount. `staleTime: 0` makes the seeded
// build-time data immediately stale, and `refetchOnMount: 'always'` forces
// a re-fetch on each mount even when cached data exists. The build-time
// prerendered HTML still ships the build-time price for first-paint (bots
// + no-JS visitors), then React Query refreshes it the moment the page
// hydrates. Acceptable trade-off until #383 invalidates the HTML itself.
const LIVE_PRICE_OPTIONS = {
    staleTime: 0,
    refetchOnMount: 'always' as const,
};

export const useProducts = (params: CustomerEquipmentTypeSearchRequest) => {
    return useQuery({
        queryKey: ['products', params],
        queryFn: () => getProducts(params),
        placeholderData: (previousData) => previousData,
        ...LIVE_PRICE_OPTIONS,
    });
};

/**
 * Infinite-scroll variant of useProducts. The first request is page 1, each
 * subsequent fetchNextPage() bumps the page param by 1 until the cumulative
 * items match totalCount. The cache key omits `page` so changing other params
 * (filters, location, dates, sort) starts a fresh, single-page query rather
 * than appending to a stale paginated buffer.
 *
 * Used by the catalogue route — replaces the old paginated useProducts +
 * Pagination component. With infinite scroll + client-side filtering for
 * per-location availability, users see a single continuous list instead of
 * pages of different sizes.
 */
export const useInfiniteProducts = (
    params: Omit<CustomerEquipmentTypeSearchRequest, 'page'>,
) => {
    return useInfiniteQuery({
        queryKey: ['products', 'infinite', params],
        queryFn: ({ pageParam }) => getProducts({ ...params, page: pageParam }),
        initialPageParam: 1,
        getNextPageParam: (lastPage, allPages) => {
            const fetched = allPages.reduce((sum, p) => sum + (p?.data?.length ?? 0), 0);
            const total = lastPage?.totalCount ?? 0;
            return fetched < total ? allPages.length + 1 : undefined;
        },
        ...LIVE_PRICE_OPTIONS,
    });
};

export const useProduct = (id: string | undefined) => {
    const { location, dateRange } = useLocationStore();
    const startDate = dateRange.from?.toISOString();
    const endDate = dateRange.to?.toISOString();

    return useQuery({
        queryKey: ['product', id, location?.id, startDate, endDate],
        queryFn: () => getProduct(id!, location?.id, startDate, endDate),
        enabled: !!id,
        // Stale-while-refetch: a date/location change puts the store values into a NEW
        // query key; without a placeholder, isLoading flips true and consumers unmount
        // into a spinner mid-interaction. Keep the previous product until fresh
        // date-scoped pricing arrives.
        placeholderData: keepPreviousData,
        ...LIVE_PRICE_OPTIONS,
    });
};

/**
 * Fetches a product by its SEO-friendly slug.
 * Supports URLs like /product/premium-baby-stroller
 */
export const useProductBySlug = (slug: string | undefined) => {
    const { location, dateRange } = useLocationStore();
    const startDate = dateRange.from?.toISOString();
    const endDate = dateRange.to?.toISOString();

    return useQuery({
        queryKey: ['product', 'slug', slug, location?.id, startDate, endDate],
        queryFn: () => getProductBySlug(slug!, location?.id, startDate, endDate),
        enabled: !!slug,
        // See useProduct — stale-while-refetch across date/location key changes.
        placeholderData: keepPreviousData,
        ...LIVE_PRICE_OPTIONS,
    });
};

/**
 * Smart hook that fetches product by either ID (GUID) or slug.
 * Automatically detects if the identifier is a GUID or slug.
 * Includes location in query key so prices refetch on location change.
 */
export const useProductByIdOrSlug = (identifier: string | undefined) => {
    const queryClient = useQueryClient();
    const { location, dateRange } = useLocationStore();
    const startDate = dateRange.from?.toISOString();
    const endDate = dateRange.to?.toISOString();
    // GUID pattern: 8-4-4-4-12 hexadecimal characters
    const isGuid = identifier ? /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(identifier) : false;

    // Stale-while-refetch: a date/location change moves the store values into a NEW
    // query key; without placeholder data, isLoading flips true for the whole refetch
    // and ProductDetail unmounts into a full-page spinner mid-interaction. Keep the
    // previous key's product, and on the very first mount (no previous key) fall back
    // to the build-time route seed, which lives under the location-less key — visitors
    // with a persisted location would otherwise start on a different key and hit the
    // spinner on every product-page load despite loader data existing.
    type ProductData = Awaited<ReturnType<typeof getProduct>>;
    const idPlaceholder = (prev: ProductData | undefined) =>
        prev ?? queryClient.getQueryData<ProductData>(['product', identifier, undefined, undefined, undefined]);
    const slugPlaceholder = (prev: ProductData | undefined) =>
        prev ?? queryClient.getQueryData<ProductData>(['product', 'slug', identifier, undefined, undefined, undefined]);

    const idQuery = useQuery({
        queryKey: ['product', identifier, location?.id, startDate, endDate],
        queryFn: () => getProduct(identifier!, location?.id, startDate, endDate),
        enabled: !!identifier && isGuid,
        placeholderData: idPlaceholder,
        ...LIVE_PRICE_OPTIONS,
    });

    const slugQuery = useQuery({
        queryKey: ['product', 'slug', identifier, location?.id, startDate, endDate],
        queryFn: () => getProductBySlug(identifier!, location?.id, startDate, endDate),
        enabled: !!identifier && !isGuid,
        placeholderData: slugPlaceholder,
        ...LIVE_PRICE_OPTIONS,
    });

    // Return the appropriate query based on identifier type
    return isGuid ? idQuery : slugQuery;
};

export const useCheckAvailability = () => {
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: CustomerEquipmentTypeAvailabilityRequest }) =>
      checkAvailability(id, data),
  });
};

export const useAvailabilityQuery = (
    id: string | undefined,
    startDate: Date | undefined,
    endDate: Date | undefined,
    quantity: number = 1
) => {
    const { location } = useLocationStore();

    return useQuery({
        queryKey: ['availability', id, startDate, endDate, quantity, location?.id],
        queryFn: () => checkAvailability(id!, {
            startDate: startDate!.toISOString(),
            endDate: endDate!.toISOString(),
            quantity,
            locationId: location?.id
        }),
        enabled: !!id && !!startDate && !!endDate,
        // Keep the previous verdict during a date/quantity change instead of flashing
        // the Add-to-Cart button disabled — the backend re-validates on add anyway.
        placeholderData: keepPreviousData,
        retry: false
    });
};

/**
 * Fetches locations where a specific equipment type has available stock.
 * Used by LocationRequiredModal to show only relevant locations.
 */
export const useAvailableLocations = (
    equipmentTypeId: string | undefined,
    startDate?: string,
    endDate?: string,
    quantity: number = 1
) => {
    return useQuery({
        queryKey: ['available-locations', equipmentTypeId, startDate, endDate, quantity],
        queryFn: () => getAvailableLocations(equipmentTypeId!, startDate, endDate, quantity),
        enabled: !!equipmentTypeId,
        staleTime: 2 * 60 * 1000, // 2 minutes (stock changes frequently)
    });
};

/**
 * Fetches related/cross-sell products for a given equipment type.
 * Returns curated recommendations plus algorithmically-matched products.
 * Includes location in query key so prices refetch on location change.
 */
export const useRelatedProducts = (
    equipmentTypeId: string | undefined,
    take: number = 8
) => {
    const { location } = useLocationStore();

    return useQuery({
        queryKey: ['related-products', equipmentTypeId, location?.id, take],
        queryFn: () => getRelatedProducts(equipmentTypeId!, location?.id, take),
        enabled: !!equipmentTypeId,
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
};
